import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Field, Input, Turnstile } from '@/components/ui';
import { useRequestOtp, useLogin } from './api';
import { authErrorMessage } from './errors';
import { setPending } from './pending';

/**
 * Email-only "send me a code" form. Used by the login page's "email me a code
 * instead" toggle (`intent: 'login'`) and by /forgot (`intent: 'reset'`).
 */
export function CodeRequestForm({ intent }: { intent: 'login' | 'reset' }) {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const requestOtp = useRequestOtp();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!token) return setErr('Complete the check below first.');
    try {
      const res = await requestOtp.mutateAsync({ email, turnstileToken: token, intent });
      setPending({
        email: email.toLowerCase().trim(),
        intent,
        purpose: intent === 'reset' ? 'reset' : 'login',
        resendAfter: res.resendAfter,
        devCode: res.devCode,
      });
      void navigate({ to: '/verify' });
    } catch (e2) {
      setErr(authErrorMessage(e2));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label="Email" error={err ?? undefined}>
        <Input
          type="email"
          required
          autoFocus
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Turnstile onToken={setToken} />
      <Button type="submit" variant="candy" size="lg" disabled={requestOtp.isPending}>
        {requestOtp.isPending ? 'Sending code…' : 'Email me a code'}
      </Button>
    </form>
  );
}

/** Signup: pick a password, then verify the email with a one-time code. */
export function SignupForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [token, setToken] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const requestOtp = useRequestOtp();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 10) return setErr('Password must be at least 10 characters.');
    if (password !== confirm) return setErr('Passwords don’t match.');
    if (!token) return setErr('Complete the check below first.');
    try {
      const res = await requestOtp.mutateAsync({
        email,
        turnstileToken: token,
        intent: 'signup',
      });
      setPending({
        email: email.toLowerCase().trim(),
        intent: 'signup',
        purpose: 'signup',
        password,
        resendAfter: res.resendAfter,
        devCode: res.devCode,
      });
      void navigate({ to: '/verify' });
    } catch (e2) {
      setErr(authErrorMessage(e2));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label="Email">
        <Input
          type="email"
          required
          autoFocus
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field label="Password" hint="at least 10 characters">
        <Input
          type="password"
          required
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Field label="Confirm password" error={err ?? undefined}>
        <Input
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>
      <Turnstile onToken={setToken} />
      <Button type="submit" variant="candy" size="lg" disabled={requestOtp.isPending}>
        {requestOtp.isPending ? 'Sending code…' : 'Send verification code'}
      </Button>
      <p className="text-xs text-ink-faint">
        Nearmirer is 18+. We’ll ask you to confirm your date of birth next.
      </p>
    </form>
  );
}

/** Everyday login: email + password. */
export function PasswordLoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const login = useLogin();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!token) return setErr('Complete the check below first.');
    try {
      const res = await login.mutateAsync({ email, password, turnstileToken: token });
      void navigate({ to: res.user.onboarded ? '/app' : '/onboarding/terms' });
    } catch (e2) {
      setErr(authErrorMessage(e2, 'Could not sign in. Try again.'));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label="Email">
        <Input
          type="email"
          required
          autoFocus
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field label="Password" error={err ?? undefined}>
        <Input
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Turnstile onToken={setToken} />
      <Button type="submit" variant="candy" size="lg" disabled={login.isPending}>
        {login.isPending ? 'Signing in…' : 'Log in'}
      </Button>
    </form>
  );
}
